import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { Box, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import '../../styles/swiper.scss';

interface PaintSwiperProps {
  children: ReactNode;
  autoPlay?: boolean;
  interval?: number;
  showArrows?: boolean;
  showIndicators?: boolean;
}

const PaintSwiper: React.FC<PaintSwiperProps> = ({
  children,
  autoPlay = false,
  interval = 3000,
  showArrows = true,
  showIndicators = true,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const itemCount = React.Children.count(children);

  const goToNext = useCallback(() => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % itemCount);
  }, [itemCount]);

  const goToPrev = useCallback(() => {
    setActiveIndex((prevIndex) => (prevIndex - 1 + itemCount) % itemCount);
  }, [itemCount]);

  const goToIndex = (index: number) => {
    setActiveIndex(index);
  };

  // 自动轮播
  useEffect(() => {
    if (!autoPlay) return;
    
    const intervalId = setInterval(goToNext, interval);
    return () => clearInterval(intervalId);
  }, [autoPlay, goToNext, interval, activeIndex]);

  // 如果没有子元素或只有一个子元素，就不显示箭头和指示器
  const shouldShowControls = itemCount > 1;

  return (
    <Box className="carousel-container">
      <Box className="carousel-wrapper">
        <Box 
          className="carousel-content-wrapper"
          sx={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {React.Children.map(children, (child, index) => (
            <Box key={index} className="carousel-item">
              {child}
            </Box>
          ))}
        </Box>

        {shouldShowControls && showArrows && (
          <>
            <IconButton 
              className="carousel-arrow carousel-arrow-left"
              onClick={goToPrev}
            >
              <ArrowBackIosNewIcon />
            </IconButton>

            <IconButton 
              className="carousel-arrow carousel-arrow-right"
              onClick={goToNext}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </>
        )}
      </Box>

      {shouldShowControls && showIndicators && (
        <Box className="carousel-indicators">
          {Array.from({ length: itemCount }).map((_, index) => (
            <Box
              key={index}
              className={`carousel-indicator ${index === activeIndex ? 'active' : ''}`}
              onClick={() => goToIndex(index)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PaintSwiper;